import { SHADOW_TRACK_STEP, STATISTIC_FILTER_SIZE } from './constants';
import { getInterpolatedOffset, meanSpeed, memoizedAscent, memoizedDescent } from './point_utils';
import type { EventState, PoiType, Point, StarterType, State } from './store';
import type TrackView from './utils/TrackView';
import Option from './utils/option';

export type StarterInfo = {
  type: 'STARTER';
  id: string;
  starter: StarterType;
  speed: number;
  speed100: number;
  offset: number;
  elevation: number;
  ascent: number;
  descent: number;
  points: Array<Point>;
};

export type PoiInfo = {
  type: 'POI';
  id: string;
  poi: PoiType;
  offset: number;
  elevation: number;
};

export function getEventById(state: State, eventId: string): Option<EventState> {
  const event = state.events.find((e) => e.eventId === eventId);
  if (event == null) return Option.none();
  return Option.of(event);
}

function getStarters(state: State, starterId: string): Array<StarterType> {
  return state.startersById.get(starterId) ?? [];
}

function filterPoints(points: Array<Point>, startTime: number, endTime: number): Array<Point> {
  return points.filter((p) => startTime <= p.time && p.time < endTime);
}

export function getStarterPoints(state: State, starter: StarterType): Option<Array<Point>> {
  const eventState = state.eventsChangeable[starter.eventIndex];
  if (eventState == null) return Option.none();
  const starterTrack = eventState.starterTracks.get(starter.id);
  if (starterTrack == null) return Option.none();
  return Option.of(starterTrack);
}

// In meters per second
function getMeanStarterSpeed(
  state: State,
  starter: StarterType,
  windowFactor = 10,
): Option<number> {
  const {
    timer: { time },
  } = state;
  const {
    options: { stepWidth },
  } = state.events[starter.eventIndex];
  const windowSize = windowFactor * stepWidth;
  return getStarterPoints(state, starter)
    .map((points) => filterPoints(points, time - windowSize, time + windowSize))
    .map((starterPoints) => meanSpeed(starterPoints));
}

function getShadowTrackByEvent(state: State, eventIndex: number): Option<TrackView> {
  return Option.of(state.events[eventIndex].chunkedShadowTrack);
}

// In meters
function getStarterOffset(state: State, starter: StarterType): Option<number> {
  return getStarterPoints(state, starter).map((starterPoints) =>
    getInterpolatedOffset(
      starterPoints,
      state.timer.time,
      state.events[starter.eventIndex].options.stepWidth,
    ),
  );
}

function getInterpolatedStatistic(
  offset: number,
  extractor: (offset: number) => Option<number>,
): Option<number> {
  if (offset < 0 || Number.isNaN(offset)) return Option.none();
  const fraction = offset % 1;
  if (fraction === 0) {
    return extractor(offset);
  }
  const offsetLo = Math.floor(offset);
  const offsetHi = Math.ceil(offset);
  return Option.zip(extractor(offsetLo), extractor(offsetHi)).map(
    ([statisticLo, statisticHi]) => statisticLo * (1 - fraction) + statisticHi * fraction,
  );
}

function getInterpolatedFilteredStatistic(track: Array<number>, index: number): number {
  const loIdx = Math.floor(index / STATISTIC_FILTER_SIZE);
  const hiIdx = Math.ceil(index / STATISTIC_FILTER_SIZE);
  const fraction = (index / STATISTIC_FILTER_SIZE) % 1;
  if (loIdx >= 0 && loIdx < track.length) {
    if (hiIdx >= 0 && hiIdx < track.length) {
      return track[loIdx] * (1 - fraction) + track[hiIdx] * fraction;
    }
    return track[loIdx];
  }
  return Number.NaN;
}

// In meters
function getElevationAtOffset(state: State, eventIndex: number, offset: number): Option<number> {
  return getShadowTrackByEvent(state, eventIndex).flatMap((shadowTrack) =>
    getInterpolatedStatistic(offset, (offset) =>
      offset < shadowTrack.size() ? Option.of(shadowTrack.getElevation(offset)) : Option.none(),
    ),
  );
}
function getAscentAndDescentAtOffset(
  state: State,
  eventIndex: number,
  offset: number,
): Option<[number, number]> {
  return getShadowTrackByEvent(state, eventIndex).flatMap((shadowTrack) => {
    const ascent = getInterpolatedFilteredStatistic(
      memoizedAscent(shadowTrack),
      offset / SHADOW_TRACK_STEP,
    );
    const descent = getInterpolatedFilteredStatistic(
      memoizedDescent(shadowTrack),
      offset / SHADOW_TRACK_STEP,
    );
    return Number.isNaN(ascent) || Number.isNaN(descent)
      ? Option.none()
      : Option.of([ascent, descent]);
  });
}

export function getPoiInfo(state: State, poi: PoiType): PoiInfo {
  const offset = poi.offset ?? Number.NaN;
  const elevation = getElevationAtOffset(state, poi.eventIndex, offset).getOrElse(() => Number.NaN);
  return { type: 'POI', id: poi.id, poi, offset, elevation };
}

export function getStarterInfo(state: State, starter: StarterType): StarterInfo {
  const points = getStarterPoints(state, starter).getOrElse(() => []);
  const meanSpeed = getMeanStarterSpeed(state, starter).getOrElse(() => Number.NaN);
  const meanSpeed100 = getMeanStarterSpeed(state, starter, 100).getOrElse(() => Number.NaN);
  const offset = getStarterOffset(state, starter).getOrElse(() => Number.NaN);
  const elevation = getElevationAtOffset(state, starter.eventIndex, offset).getOrElse(
    () => Number.NaN,
  );
  const [ascent, descent] = getAscentAndDescentAtOffset(
    state,
    starter.eventIndex,
    offset,
  ).getOrElse(() => [Number.NaN, Number.NaN]);
  return {
    type: 'STARTER',
    id: starter.id,
    starter,
    speed: meanSpeed,
    speed100: meanSpeed100,
    offset,
    elevation,
    ascent,
    descent,
    points,
  };
}

export function getStarterInfos(state: State, starterId: string): Array<StarterInfo> {
  return getStarters(state, starterId).map((starter) => getStarterInfo(state, starter));
}
